import { ArrowDown, Check, ChevronRight, VideotapeIcon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../shared/components/Icon';
import './SetupVpnCard.css';

export interface AppProviderOptions {
    appName: string;
    videoInstruction?: string;
    steps: React.ReactElement[];
}

export interface SetupVpnCardOptionsProps {
    platform: string;
    iconName: string;
    iconSize?: number;
    providers: AppProviderOptions[];
    autoExpand?: boolean;
}

const utilIconSize = 30;

export const SetupVpnCard: React.FC<SetupVpnCardOptionsProps> = ({
    autoExpand,
    platform,
    iconName,
    iconSize = 32,
    providers,
}) => {
    const [expanded, setExpanded] = useState(autoExpand);
    const [maxHeight, setMaxHeight] = useState('0px');
    const contentRef = useRef<HTMLDivElement>(null);
    const [showVideo, setShowVideo] = useState(false);
    const [videoSrc, setVideoSrc] = useState<string | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);

    const hasMultipleProviders = providers.length > 1;
    const [selectedProvider, setSelectedProvider] = useState<AppProviderOptions>(providers[0]);
    const [activeStepIndex, setActiveStepIndex] = useState(0);

    useEffect(() => {
        setActiveStepIndex(0);
    }, [selectedProvider]);

    const recalcHeight = () => {
        if (expanded && contentRef.current) {
            setTimeout(() => {
                setMaxHeight(`${contentRef.current!.scrollHeight}px`);
            }, 100);
        }
    };

    useEffect(() => {
        if (expanded && contentRef.current) {
            setMaxHeight(`${contentRef.current.scrollHeight}px`);
        } else {
            setMaxHeight('0px');
        }
    }, [expanded, selectedProvider, activeStepIndex]);

    const toggle = () => setExpanded((prev) => !prev);

    const totalSteps = selectedProvider.steps.length;
    const progressPercent = totalSteps > 1
        ? ((activeStepIndex + 1) / totalSteps) * 100
        : 100;

    return (
        <>
            <div className="vpn-card glass-ui">
                <div
                    style={expanded ? {} : { borderBottom: 'none' }}
                    className="vpn-setting-header"
                    role="button"
                    tabIndex={0}
                    onClick={toggle}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(); }}
                >
                    <div className="vpn-setting-title">
                        {platform}
                        <Icon name={iconName} size={iconSize} />
                    </div>
                    <div className="vpn-setting-right-icons">
                        {selectedProvider.videoInstruction && (
                            <button
                                className="vpn-video-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setVideoSrc(`/assets/${selectedProvider.videoInstruction}`);
                                    setShowVideo(true);
                                }}
                                title="Смотреть видео-инструкцию"
                            >
                                <span className="vpn-video-btn-label">Видео</span>
                                <VideotapeIcon size={utilIconSize} />
                            </button>
                        )}
                        <button
                            style={{background: 'none', border: 'none'}}
                            className={`arrow ${expanded ? 'rotated' : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggle(); }}
                            aria-label={expanded ? 'Свернуть' : 'Развернуть'}
                        >
                            <ArrowDown size={utilIconSize} />
                        </button>
                    </div>
                </div>

                <div ref={contentRef} className="vpn-steps animated" style={{ maxHeight }}>
                    <div className="vpn-progress-bar-wrap">
                        <div className="vpn-progress-bar-fill" style={{ width: `${progressPercent}%` }} />
                    </div>

                    {hasMultipleProviders && (
                        <div className="vpn-provider-select">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label className="vpn-provider-select-label" htmlFor="vpn-provider">
                                    Доступно несколько вариантов установки:
                                </label>
                                <select
                                    id="vpn-provider"
                                    value={selectedProvider.appName}
                                    onChange={(e) => {
                                        const found = providers.find((p) => p.appName === e.target.value);
                                        if (found) setSelectedProvider(found);
                                    }}
                                >
                                    {providers.map((provider) => (
                                        <option key={provider.appName} value={provider.appName}>
                                            {provider.appName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {selectedProvider.steps.map((step, index) => {
                        if (index > activeStepIndex) return null;

                        const isCompleted = index < activeStepIndex;
                        const isCurrent = index === activeStepIndex;
                        const isLast = index === totalSteps - 1;

                        return (
                            <div
                                key={(step as React.ReactElement).key ?? `step-${index}`}
                                className={[
                                    'vpn-step-block',
                                    index === 0 ? 'first' : '',
                                    isCompleted ? 'completed' : '',
                                    isCurrent ? 'active' : '',
                                ].filter(Boolean).join(' ')}
                            >
                                <div className="vpn-step-row">
                                    <div className={`vpn-step-badge ${isCompleted ? 'done' : 'current'}`}>
                                        {isCompleted ? <Check size={13} strokeWidth={3} /> : index + 1}
                                    </div>
                                    <div className="vpn-step-content">
                                        {React.cloneElement(step as React.ReactElement<any>, {
                                            onExpandContent: recalcHeight,
                                            onOpenImage: (img: string) => setImageSrc(img),
                                        })}
                                        {isCurrent && !isLast && (
                                            <button
                                                className="vpn-step-next-btn"
                                                onClick={() => setActiveStepIndex((prev) => prev + 1)}
                                            >
                                                Далее <ChevronRight color='var(--color-on-accent)' size={16} />
                                            </button>
                                        )}
                                        {isCurrent && isLast && (
                                            <div className="vpn-step-done-badge">
                                                <Check size={15} strokeWidth={2.5} />
                                                Всё готово!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {showVideo && videoSrc && (
                <div
                    className="video-modal-overlay"
                    role="button"
                    tabIndex={0}
                    aria-label="Закрыть видео"
                    onClick={() => { setShowVideo(false); setVideoSrc(null); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') { setShowVideo(false); setVideoSrc(null); } }}
                >
                    <div className="video-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="video-modal-close"
                            onClick={() => { setShowVideo(false); setVideoSrc(null); }}
                            aria-label="Закрыть видео"
                        >
                            ×
                        </button>
                        <video width="100%" height="100%" autoPlay controls>
                            <source src={videoSrc} type="video/webm" />
                            <track kind="captions" />
                            Ваш браузер не поддерживает видео.
                        </video>
                    </div>
                </div>
            )}

            {imageSrc && (
                <div
                    className="video-modal-overlay"
                    role="button"
                    tabIndex={0}
                    aria-label="Закрыть изображение"
                    onClick={() => setImageSrc(null)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setImageSrc(null); }}
                >
                    <div className="image-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="video-modal-close"
                            onClick={() => setImageSrc(null)}
                            aria-label="Закрыть изображение"
                        >
                            ×
                        </button>
                        <img
                            src={imageSrc}
                            alt="Полноразмерная инструкция"
                            className="image-modal-content"
                        />
                    </div>
                </div>
            )}
        </>
    );
};
